import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom';
import SignIn from './signin';
import MasterAccount from './masteraccount';
import MusicUploader from './musicuploader';
import MusicHistory from './musichistory';
import AccountPage from './accountpage';
import MasterAccountsDashboard from './masteraccountsdashboard';
import './App.css';
import backgroundImage from './images/sign-up-desk-background-image.jpg';
import logo from './images/KrossKast_Logo-red.png';
import favicon from './images/kk-favicon.png';
import faviconWhite from './images/kk-favicon-white.png';
import confirmEmailIllustration from './images/illustration_confirm_email.png';
import eyeLookIcon from './images/icons/eye-look-icon.svg';
import eyeHiddenIcon from './images/icons/eye-hidden-icon.svg';
import checkMarkIcon from './images/icons/check-mark-line-icon.svg';
import ProtectedRoute from './components/ProtectedRoute';
import Locations from './locations';
import Channels from './channels';
import PlayerBar from './components/PlayerBar';
import TalentRequest from './talentrequest';
import Sidebar from './components/Sidebar';
import InProduction from './inproduction';
import MessagingSchedule from './messagingschedule';
import Billing from './billing';
import Support from './support';
import FAQ from './faq';
import ApiService from './services/api';
// Custom authentication - no Auth0 hooks needed

function VendorSignupSidebar({ currentStep, personalFormCompleted, setCurrentStep }) {
  const navigate = useNavigate();

  // Helper function to determine if a step is completed for navigation
  const isStepCompletedForNavigation = (step) => {
    if (currentStep === 'confirmation') return false;
    
    if (step === 'personal') return currentStep !== 'personal';
    if (step === 'company') return currentStep === 'confirmation';
    return false;
  };

  // Helper function to determine if a step is completed for visual display
  const isStepCompletedForDisplay = (step) => {
    if (step === 'personal') return currentStep !== 'personal';
    if (step === 'company') return currentStep === 'confirmation';
    return false;
  };

  // Helper function to handle step navigation
  const handleStepClick = (step) => {
    if (currentStep === 'confirmation') return;
    
    if (isStepCompletedForNavigation(step)) {
      setCurrentStep(step);
    }
  };

  // Helper function to render the appropriate icon
  const renderStepIcon = (step) => {
    if (currentStep === step) {
      return <img src={faviconWhite} alt="" className="nav-icon" style={{ marginLeft: '12px', marginRight: '19px' }} />;
    }
    if (isStepCompletedForDisplay(step)) {
      return (
        <span style={{
          display: 'inline-block',
          width: '17px',
          height: '17px',
          marginRight: '24px',
          marginLeft: '15px',
          verticalAlign: 'middle',
          position: 'relative'
        }}>
          <img 
            src={checkMarkIcon}
            alt=""
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '36px',
              height: '36px',
              filter: 'brightness(0) invert(1)',
              display: 'block'
            }}
          />
        </span>
      );
    }
    return (
      <span style={{
        display: 'inline-block',
        width: '10px',
        height: '10px',
        background: '#FFFFFF',
        borderRadius: '50%',
        marginRight: '24px',
        marginLeft: '23px',
        verticalAlign: 'middle'
      }} />
    );
  };

  return (
    <div className="sidebar vendor-signup-sidebar">
      <div>
        <nav style={{ position: 'relative', height: '220px', marginTop: '40px' }}>
          <div 
            className={`nav-item ${currentStep === 'personal' ? 'active' : ''}`}
            style={{ 
              cursor: currentStep === 'confirmation' ? 'default' : (isStepCompletedForNavigation('personal') ? 'pointer' : 'not-allowed'),
              position: 'relative',
              zIndex: 1,
              marginBottom: '8px',
            }}
            onClick={() => handleStepClick('personal')}
          >
            {renderStepIcon('personal')}
            Personal Info
          </div>
          <div 
            className={`nav-item ${currentStep === 'company' ? 'active' : ''}`}
            style={{ 
              cursor: currentStep === 'confirmation' ? 'default' : (isStepCompletedForNavigation('company') ? 'pointer' : (personalFormCompleted ? 'pointer' : 'not-allowed')),
              opacity: personalFormCompleted ? 1 : 0.5,
              position: 'relative',
              zIndex: 1,
              color: '#FFFFFF',
              marginBottom: '8px',
            }}
            onClick={() => handleStepClick('company')}
          >
            {renderStepIcon('company')}
            Company Info
          </div>
          <div 
            className={`nav-item ${currentStep === 'confirmation' ? 'active' : ''}`}
            style={{
              position: 'relative',
              zIndex: 1,
              color: '#FFFFFF',
              marginBottom: '8px',
              opacity: currentStep === 'confirmation' ? 1 : 0.5,
              cursor: 'default'
            }}
          >
            {renderStepIcon('confirmation')}
            Confirmation Email
          </div>
          <div 
            className="nav-item"
            style={{
              position: 'relative',
              zIndex: 1,
              color: '#FFFFFF',
              marginBottom: '8px',
              opacity: 0.5,
              cursor: 'default'
            }}
          >
            <span style={{
              display: 'inline-block',
              width: '10px',
              height: '10px',
              background: '#FFFFFF',
              borderRadius: '50%',
              marginRight: '24px',
              marginLeft: '23px',
              verticalAlign: 'middle'
            }} />
            In Review
          </div>
        </nav>
      </div>
    </div>
  );
}

const VendorSignup = () => {
  const [currentStep, setCurrentStep] = useState('personal'); // Add state for current step
  
  // Custom authentication - no Auth0 hooks needed
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    jobTitle: '',
    phoneNumber: '',
    email: '',
    emailConfirmation: '',
    password: '',
    passwordConfirmation: '',
    companyName: '',
    companyWebsite: '',
    companyPhoneNumber: '',
    vendorType: '',
    companyAddress: '',
    city: '',
    state: '',
    zipCode: '',
    termsAccepted: false
  });
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [personalFormCompleted, setPersonalFormCompleted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const navigate = useNavigate();

  document.documentElement.style.setProperty('--background-image', `url(${backgroundImage})`);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // For phone numbers, only allow numbers and format with hyphens
    if (name === 'phoneNumber' || name === 'companyPhoneNumber') {
      // Remove any non-digit characters
      const numbersOnly = value.replace(/[^\d]/g, '');
      
      // Format US phone number (xxx-xxx-xxxx)
      let formattedNumber = numbersOnly;
      if (numbersOnly.length <= 3) {
        formattedNumber = numbersOnly;
      } else if (numbersOnly.length <= 6) {
        formattedNumber = `${numbersOnly.slice(0, 3)}-${numbersOnly.slice(3)}`;
      } else {
        formattedNumber = `${numbersOnly.slice(0, 3)}-${numbersOnly.slice(3, 6)}-${numbersOnly.slice(6, 10)}`;
      }

      setFormData(prev => ({
        ...prev,
        [name]: formattedNumber
      }));
      return;
    }

    // Handle other fields normally
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Validate emails when typing in either email field
    if (name === 'email' || name === 'emailConfirmation') {
      const updatedEmail = name === 'email' ? value : formData.email;
      const updatedConfirmation = name === 'emailConfirmation' ? value : formData.emailConfirmation;
      
      if (updatedEmail && updatedConfirmation) {
        if (updatedEmail !== updatedConfirmation) {
          setEmailError('Email addresses must match');
        } else {
          setEmailError('');
        }
      }
    }

    // Validate passwords when typing in either password field
    if (name === 'password' || name === 'passwordConfirmation') {
      const updatedPassword = name === 'password' ? value : formData.password;
      const updatedConfirmation = name === 'passwordConfirmation' ? value : formData.passwordConfirmation;
      
      if (updatedPassword && updatedConfirmation) {
        if (updatedPassword !== updatedConfirmation) {
          setPasswordError('Passwords must match');
        } else {
          setPasswordError('');
        }
      }
    }
  };

  // Prevent moving to password fields if emails don't match
  const handlePasswordFieldFocus = (e) => {
    if (formData.email && formData.emailConfirmation && 
        formData.email !== formData.emailConfirmation) {
      e.preventDefault();
      setEmailError('Email addresses must match');
      // Focus back on email confirmation field
      document.querySelector('input[name="emailConfirmation"]').focus();
    }
  };

  const handlePasswordFocus = (e) => {
    setIsPasswordFocused(true);
    handlePasswordFieldFocus(e);
  };

  const handlePasswordVisibilityToggle = () => {
    setShowPassword(!showPassword);
    if (!isPasswordFocused) {
      setIsPasswordFocused(true);
    }
  };

  const isFormValid = () => {
    return (
      formData.firstName &&
      formData.lastName &&
      formData.jobTitle &&
      formData.phoneNumber &&
      formData.email &&
      formData.emailConfirmation &&
      formData.password &&
      formData.passwordConfirmation &&
      formData.email === formData.emailConfirmation &&
      formData.password === formData.passwordConfirmation &&
      formData.companyName &&
      formData.companyWebsite &&
      formData.companyPhoneNumber &&
      formData.vendorType &&
      formData.companyAddress &&
      formData.city &&
      formData.state &&
      formData.zipCode &&
      formData.termsAccepted
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    let hasError = false;

    // Check if emails match
    if (formData.email !== formData.emailConfirmation) {
      setEmailError('Email addresses must match');
      hasError = true;
    }

    // Check if passwords match
    if (formData.password !== formData.passwordConfirmation) {
      setPasswordError('Passwords must match');
      hasError = true;
    }

    // Don't proceed if there are any errors
    if (hasError) {
      return;
    }

    // If all validations pass, proceed to company info
    setPersonalFormCompleted(true);
    setCurrentStep('company');
    document.querySelector('.nav-item.active').classList.remove('active');
    document.querySelectorAll('.nav-item')[1].classList.add('active');
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    
    // Clear any previous errors
    setSubmitError('');
    setIsSubmitting(true);

    try {
      // Send vendor signup data to Admin Area API
      const response = await ApiService.registerVendor(formData);
      
      console.log('Vendor registration successful:', response);
      
      // Move to confirmation step
      setCurrentStep('confirmation');
      // Update nav item
      document.querySelector('.nav-item.active').classList.remove('active');
      document.querySelectorAll('.nav-item')[2].classList.add('active');
      
    } catch (error) {
      console.error('Vendor registration failed:', error);
      setSubmitError(error.message || 'Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNavClick = (step) => {
    // Only allow navigation if not on confirmation step
    if (currentStep !== 'confirmation') {
      // Only allow navigation to company info if personal info is completed
      if (step === 'company' && !personalFormCompleted) {
        return;
      }
      setCurrentStep(step);
    }
  };

  // First, let's create a consistent style object for all messages
  const messageStyle = {
    fontSize: '12px',
    color: '#000000',
    marginTop: '4px'
  };

  // Add this to the existing style objects
  const labelStyle = {
    fontSize: '14px'
  };

  // Create a common input style object
  const inputStyle = {
    fontSize: '14px'
  };

  return (
    <div className="App">
      <div className="top-nav">
        <img src={logo} alt="KrossKast" className="nav-logo" />
        <div className="user-nav">
          <button 
            className="signin-button"
            onClick={() => navigate('/signin')}
          >
            Sign In
          </button>
        </div>
      </div>
      <div className="master-content">
        <VendorSignupSidebar 
          currentStep={currentStep} 
          personalFormCompleted={personalFormCompleted} 
          setCurrentStep={setCurrentStep}
        />
        <div className="main-content vendor-signup-main-content">
          <div className="form-container" style={currentStep === 'confirmation' ? {width: '700px'} : undefined}>
            {currentStep === 'personal' ? (
              <>
                <h1 style={{fontSize: '22px'}}>Vendor Sign Up Form</h1>
                <p style={{fontSize: '14px', marginTop: '-1px', color: '#000000'}}>Enter your information below to sign up for KrossKast.</p>
                
                <form onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label style={labelStyle}>First Name<span className="required">*</span></label>
                      <input 
                        type="text" 
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        style={inputStyle}
                        required 
                      />
                    </div>
                    <div className="form-group">
                      <label style={labelStyle}>Last Name<span className="required">*</span></label>
                      <input 
                        type="text" 
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        style={inputStyle}
                        required 
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label style={labelStyle}>Job Title<span className="required">*</span></label>
                      <input 
                        type="text" 
                        name="jobTitle"
                        value={formData.jobTitle}
                        onChange={handleInputChange}
                        style={inputStyle}
                        required 
                      />
                    </div>
                    <div className="form-group">
                      <label style={labelStyle}>Phone Number<span className="required">*</span></label>
                      <div className="phone-input">
                        <select className="country-code" style={{width: '70px', marginRight: '6px', fontSize: '14px'}} required>
                          <option value="+1">🇺🇸 +1</option>
                        </select>
                        <input 
                          type="tel" 
                          name="phoneNumber"
                          value={formData.phoneNumber}
                          onChange={handleInputChange}
                          pattern="[0-9-]*"
                          inputMode="numeric"
                          maxLength="12"
                          style={{...inputStyle, paddingLeft: '75px'}}
                          required 
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label style={labelStyle}>Email<span className="required">*</span></label>
                      <input 
                        type="email" 
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        style={inputStyle}
                        required 
                      />
                    </div>
                    <div className="form-group">
                      <label style={labelStyle}>Email Confirmation<span className="required">*</span></label>
                      <input 
                        type="email" 
                        name="emailConfirmation"
                        value={formData.emailConfirmation}
                        onChange={handleInputChange}
                        style={inputStyle}
                        required 
                      />
                      {emailError && <div style={{
                        fontSize: '12px',
                        color: '#000000',
                        marginTop: '4px'
                      }}>{emailError}</div>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label style={labelStyle}>Password<span className="required">*</span></label>
                      <div style={{ position: 'relative' }}>
                        <input 
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          onFocus={handlePasswordFocus}
                          minLength="8"
                          style={inputStyle}
                          required 
                        />
                        <span 
                          onClick={handlePasswordVisibilityToggle}
                          style={{
                            position: 'absolute',
                            right: '10px',
                            top: '55%',
                            transform: 'translateY(-50%)',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}
                        >
                          {showPassword ? <img src={eyeLookIcon} alt="hide" style={{width: '20px', height: '20px'}} /> : <img src={eyeHiddenIcon} alt="show" style={{width: '20px', height: '20px'}} />}
                        </span>
                      </div>
                      {isPasswordFocused && formData.password.length < 8 && (
                        <div style={messageStyle}>
                          Password must be at least 8 characters
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label style={labelStyle}>Password Confirmation<span className="required">*</span></label>
                      <div style={{ position: 'relative' }}>
                        <input 
                          type={showConfirmPassword ? "text" : "password"}
                          name="passwordConfirmation"
                          value={formData.passwordConfirmation}
                          onChange={handleInputChange}
                          onFocus={handlePasswordFieldFocus}
                          style={inputStyle}
                          required 
                        />
                        <span 
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          style={{
                            position: 'absolute',
                            right: '10px',
                            top: '55%',
                            transform: 'translateY(-50%)',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}
                        >
                          {showConfirmPassword ? <img src={eyeLookIcon} alt="hide" style={{width: '20px', height: '20px'}} /> : <img src={eyeHiddenIcon} alt="show" style={{width: '20px', height: '20px'}} />}
                        </span>
                      </div>
                      {passwordError && <div style={{
                        fontSize: '12px',
                        color: '#000000',
                        marginTop: '4px'
                      }}>{passwordError}</div>}
                    </div>
                  </div>

                  <button type="submit" className="continue-btn">
                    Continue
                  </button>
                </form>
              </>
            ) : currentStep === 'company' ? (
              <>
                <h1 style={{fontSize: '22px'}}>Company Information</h1>
                <p style={{fontSize: '14px', marginTop: '-1px', color: '#000000'}}>Before proceeding, we'll need to know your company's contact details.</p>
                
                <form onSubmit={handleCompanySubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label style={labelStyle}>Company Name<span className="required">*</span></label>
                      <input 
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        style={inputStyle}
                        required 
                      />
                    </div>
                    <div className="form-group">
                      <label style={labelStyle}>Company Phone Number<span className="required">*</span></label>
                      <div className="phone-input">
                        <select className="country-code" style={{width: '70px', marginRight: '6px', fontSize: '14px'}} required>
                          <option value="+1">🇺🇸 +1</option>
                        </select>
                        <input 
                          type="tel" 
                          name="companyPhoneNumber"
                          value={formData.companyPhoneNumber}
                          onChange={handleInputChange}
                          pattern="[0-9-]*"
                          inputMode="numeric"
                          maxLength="12"
                          style={{...inputStyle, paddingLeft: '75px'}}
                          required 
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label style={labelStyle}>Company Website<span className="required">*</span></label>
                      <input 
                        type="text"
                        name="companyWebsite"
                        value={formData.companyWebsite}
                        onChange={handleInputChange}
                        style={inputStyle}
                        required 
                      />
                    </div>
                    <div className="form-group">
                      <label style={labelStyle}>Vendor Type<span className="required">*</span></label>
                      <select
                        name="vendorType"
                        value={formData.vendorType}
                        onChange={handleInputChange}
                        style={inputStyle}
                        required
                      >
                        <option value="">Select a vendor type</option>
                        <option value="audio_installer">Audio Installer</option>
                        <option value="music_pool">Music Pool</option>
                        <option value="record_label">Record Label</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group full-width">
                      <label style={labelStyle}>Company Street Address<span className="required">*</span></label>
                      <input 
                        type="text"
                        name="companyAddress"
                        value={formData.companyAddress}
                        onChange={handleInputChange}
                        style={inputStyle}
                        required 
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label style={labelStyle}>City<span className="required">*</span></label>
                      <input 
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        style={inputStyle}
                        required 
                      />
                    </div>
                    <div className="form-group">
                      <label style={labelStyle}>State<span className="required">*</span></label>
                      <select
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        style={inputStyle}
                        required
                      >
                        <option value="">Select a state</option>
                        <option value="AL">Alabama</option>
                        <option value="AK">Alaska</option>
                        <option value="AZ">Arizona</option>
                        <option value="AR">Arkansas</option>
                        <option value="CA">California</option>
                        <option value="CO">Colorado</option>
                        <option value="CT">Connecticut</option>
                        <option value="DE">Delaware</option>
                        <option value="FL">Florida</option>
                        <option value="GA">Georgia</option>
                        <option value="HI">Hawaii</option>
                        <option value="ID">Idaho</option>
                        <option value="IL">Illinois</option>
                        <option value="IN">Indiana</option>
                        <option value="IA">Iowa</option>
                        <option value="KS">Kansas</option>
                        <option value="KY">Kentucky</option>
                        <option value="LA">Louisiana</option>
                        <option value="ME">Maine</option>
                        <option value="MD">Maryland</option>
                        <option value="MA">Massachusetts</option>
                        <option value="MI">Michigan</option>
                        <option value="MN">Minnesota</option>
                        <option value="MS">Mississippi</option>
                        <option value="MO">Missouri</option>
                        <option value="MT">Montana</option>
                        <option value="NE">Nebraska</option>
                        <option value="NV">Nevada</option>
                        <option value="NH">New Hampshire</option>
                        <option value="NJ">New Jersey</option>
                        <option value="NM">New Mexico</option>
                        <option value="NY">New York</option>
                        <option value="NC">North Carolina</option>
                        <option value="ND">North Dakota</option>
                        <option value="OH">Ohio</option>
                        <option value="OK">Oklahoma</option>
                        <option value="OR">Oregon</option>
                        <option value="PA">Pennsylvania</option>
                        <option value="RI">Rhode Island</option>
                        <option value="SC">South Carolina</option>
                        <option value="SD">South Dakota</option>
                        <option value="TN">Tennessee</option>
                        <option value="TX">Texas</option>
                        <option value="UT">Utah</option>
                        <option value="VT">Vermont</option>
                        <option value="VA">Virginia</option>
                        <option value="WA">Washington</option>
                        <option value="WV">West Virginia</option>
                        <option value="WI">Wisconsin</option>
                        <option value="WY">Wyoming</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label style={labelStyle}>Zip Code<span className="required">*</span></label>
                      <input 
                        type="text"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        pattern="[0-9]*"
                        maxLength="5"
                        style={inputStyle}
                        required 
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group full-width">
                      <div className="checkbox-container">
                        <input
                          type="checkbox"
                          id="terms"
                          name="termsAccepted"
                          checked={formData.termsAccepted}
                          onChange={handleInputChange}
                          style={inputStyle}
                          required
                        />
                        <label htmlFor="terms">
                          I accept and agree to KrossKast's <a href="https://accounts.krosskast.com/terms-and-conditions" className="link" style={{color: '#000000', textDecoration: 'underline'}} target="_blank" rel="noopener noreferrer">Terms and Conditions</a> and <a href="https://accounts.krosskast.com/privacy-policy" className="link" style={{color: '#000000', textDecoration: 'underline'}} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                        </label>
                      </div>
                    </div>
                  </div>

                  {submitError && (
                    <div style={{
                      backgroundColor: '#fee',
                      border: '1px solid #fcc',
                      borderRadius: '4px',
                      padding: '12px',
                      marginBottom: '16px',
                      color: '#c33',
                      fontSize: '14px'
                    }}>
                      {submitError}
                    </div>
                  )}

                  <button type="submit" className="continue-btn" disabled={isSubmitting}>
                    {isSubmitting ? 'Creating Account...' : 'Sign Up'}
                  </button>
                </form>
              </>
            ) : (
              <>
                <div style={{textAlign: 'center', maxWidth: '500px', margin: '0 auto'}}>
                  <img 
                    src={confirmEmailIllustration} 
                    alt="Email confirmation illustration" 
                    style={{
                      maxWidth: '160px',
                      marginBottom: '40px',
                      display: 'block',
                      margin: '24px auto 40px auto'
                    }}
                  />
                  <h1 style={{textAlign: 'center', width: '100%', fontSize: '22px', marginBottom: '24px'}}>A confirmation email has been sent!</h1>
                  <p style={{textAlign: 'center', fontSize: '14px'}}>
                    We've sent a confirmation email to <strong>{formData.email}</strong>. 
                    Please check your inbox and click the confirmation link to complete your registration.
                  </p>
                  <p style={{textAlign: 'center', fontSize: '14px', marginTop: '-8px', marginBottom: '-8px'}}>
                    If you don't see the email, please check your spam folder. Contact us <u><a href="https://www.krosskast.com/contact" className="link" style={{color: '#000000'}} target="_blank" rel="noopener noreferrer">here</a></u> if you need assistance.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorSignup; 